import get from 'growth-payments-core/object/get';
import memoizeOne from 'react-utils/memoizeOne';
import { ACTIVE_CQT_PORTAL, COMMERCE_OVERVIEW_REVAMP, CPQ_QUOTES_GATE, CPQ_UPGRADES_GATE, KYC_EMBEDDED_FALLBACK_EXPERIENCE, NEW_PORTAL_CPQ_EXPERIENCE, PRE_APPROVED, PRODUCT_BUILDER, PRODUCT_BUILDER_IFRAME_GATE, QUOTES_STATUS_DEALS_INDEX } from '../constants/gates';
import { useOptinAvailability } from '../hooks/useOptinAvailability';
import { useUserInfo } from '../hooks/useUserInfo';
import { getAuth } from './user';
// @ts-expect-error - Untyped module
import { OPTIN_VIEW_NEW } from 'ui-addon-opt/constants/UIOptConstants';
import useHasCPQCreateScope from '../hooks/quotes/useHasCPQCreateScope';
import useHasCPQScope from '../hooks/quotes/useHasCPQScope';
import { Scopes } from './scopes';
const EMPTY_GATES = new Set();

// gates don't change over the lifetime of the app. use a memoized helper to
// ensure that all calls to the `useGetIsUngated` hook share one Set,
// instead of each hook call/component building its own
const memoizedToGatesSet = memoizeOne(gates => new Set(gates));
export const useGates = () => {
  const userInfo = useUserInfo();
  const userInfoGates = !userInfo ? EMPTY_GATES : memoizedToGatesSet(userInfo.gates);
  return userInfoGates;
};
export const getGates = () => {
  return get(getAuth(), 'gates');
};
export const useIsUngated = (...gatesToCheck) => {
  const gates = useGates();
  return gatesToCheck.every(gate => gates.has(gate));
};
export const useIsOptedIn = optInName => {
  const {
    data: optinView
  } = useOptinAvailability(optInName);
  return (optinView === null || optinView === void 0 ? void 0 : optinView.optinAvailability) === OPTIN_VIEW_NEW;
};
export const useIsOptedInWithLoading = optInName => {
  const {
    data: optinView,
    loading
  } = useOptinAvailability(optInName);
  return {
    isOptedIn: (optinView === null || optinView === void 0 ? void 0 : optinView.optinAvailability) === OPTIN_VIEW_NEW,
    isLoading: loading
  };
};
export const useIsUngatedForPreApproval = () => useIsUngated(PRE_APPROVED);
export const useIsUngatedForKycEmbeddedFallbackExperience = () => useIsUngated(KYC_EMBEDDED_FALLBACK_EXPERIENCE);
export const useIsUngatedForQuotesStatusDealsIndex = () => useIsUngated(QUOTES_STATUS_DEALS_INDEX);
export const useIsUngatedForCommerceOverviewRevamp = () => useIsUngated(COMMERCE_OVERVIEW_REVAMP);

/**
 * CPQ Quotes utils
 * Relevant slack post - https://hubspot.slack.com/archives/C08P01MV477/p1748643647254359?thread_ts=1748530241.285839&cid=C08P01MV477
 */
export const OPT_IN_NAME = 'cpq-quote-private-beta';
export const useIsUngatedForCpqQuotes = () => useIsUngated(CPQ_QUOTES_GATE);
export const useHasCPQBetaWithLoading = () => {
  const isUngatedForCPQBeta = useIsUngated(CPQ_QUOTES_GATE);
  const {
    isOptedIn,
    isLoading
  } = useIsOptedInWithLoading(OPT_IN_NAME);
  return {
    hasCPQBeta: isUngatedForCPQBeta && isOptedIn,
    isLoading
  };
};
export const useHasCPQBeta = () => {
  const {
    hasCPQBeta
  } = useHasCPQBetaWithLoading();
  return hasCPQBeta;
};
export const useHasCPQQuoteCreateWithLoading = () => {
  const {
    hasCPQBeta,
    isLoading
  } = useHasCPQBetaWithLoading();
  const hasCPQQuoteCreate = useHasCPQCreateScope();
  return {
    hasCPQQuoteCreate: hasCPQBeta || hasCPQQuoteCreate,
    isLoading
  };
};
export const useHasCPQQuoteCreate = () => {
  const {
    hasCPQQuoteCreate
  } = useHasCPQQuoteCreateWithLoading();
  return hasCPQQuoteCreate;
};
export const useHasCPQQuoteAccessWithLoading = () => {
  const {
    hasCPQBeta,
    isLoading
  } = useHasCPQBetaWithLoading();
  const hasCPQQuoteAccess = useHasCPQScope();
  return {
    hasCPQQuoteAccess: hasCPQBeta || hasCPQQuoteAccess,
    isLoading
  };
};
export const useHasCPQQuoteAccess = () => {
  const {
    hasCPQQuoteAccess
  } = useHasCPQQuoteAccessWithLoading();
  return hasCPQQuoteAccess;
};
export const useHasCPQQuoteCreateWithFeature = featureGate => {
  const hasCPQ = useHasCPQQuoteCreate();
  const isUngatedForFeature = useIsUngated(featureGate);
  return hasCPQ && isUngatedForFeature;
};
export const useHasCPQQuoteAccessWithFeature = featureGate => {
  const hasCPQ = useHasCPQQuoteAccess();
  const isUngatedForFeature = useIsUngated(featureGate);
  return hasCPQ && isUngatedForFeature;
};
export const useIsUngatedForCPQQuotesAndOptedIn = () => {
  return useHasCPQQuoteAccess();
};
export const useIsUngatedForCPQFeature = featureGate => {
  return useHasCPQQuoteAccessWithFeature(featureGate);
};
export const useIsUngatedForProductBuilder = () => {
  const isUngatedForProductBuilder = useIsUngated(PRODUCT_BUILDER);
  const isUngatedForCpqQuotes = useIsUngatedForCpqQuotes();
  return isUngatedForProductBuilder || isUngatedForCpqQuotes;
};
export const useIsUngatedForProductBuilderIframe = () => useIsUngated(PRODUCT_BUILDER_IFRAME_GATE);
export const useIsUngatedForCpqUpgrades = () => useIsUngated(CPQ_UPGRADES_GATE);

/**
 * @description
 * Determines if user is active quotes user.
 * If user is ungated that means they have published a legacy quote in the 6 months prior to Inbound 2025
 * More details in the https://git.hubteam.com/HubSpot/commerce-quotes-management-team/issues/548
 **/
export const useIsActiveCQTPortal = () => {
  return useIsUngated(ACTIVE_CQT_PORTAL);
};

/**
 * @description
 * Determines if user is ungated for the new portal CPQ experience.
 * A core seat user on a "new" portal will not have access to legacy quotes, whereas
 * a core seat user on a "legacy" portal will. This gate gives will be turned on as a fulcrum and
 * will give us more control if we want to override the experience for specific portals
 **/
export const useIsPostInbound2025Portal = () => useIsUngated(NEW_PORTAL_CPQ_EXPERIENCE);

/**
 * @example
 * @deprecated - use getIsUngatedForCPQFeatureWithCreateScope or getIsUngatedForCPQFeatureWithAccessScope instead
 * // Check if user is ungated and opted into CPQ quotes. If isOptedIn is not provided, it will default to true (essentially just validating gates)
 * const gates = ['commerce:cpq:bse:CPQQuote','some-feature-gate'];
 * const isOptedIn = useIsOptedIn(OPT_IN_NAME);
 * const isUngatedForCPQFeature = getIsUngatedForCPQFeature(gates, 'some-feature-gate', isOptedIn);
 */
export const getIsUngatedForCPQFeature = (gates, featureGate, isOptedIn) => {
  return gates.includes(CPQ_QUOTES_GATE) && gates.includes(featureGate) && (isOptedIn !== null && isOptedIn !== void 0 ? isOptedIn : true);
};
export const getHasCPQQuoteCreateWithFeature = (gates, featureGate, scopes, isOptedIn) => {
  const hasCPQBeta = gates.includes(CPQ_QUOTES_GATE) && (isOptedIn !== null && isOptedIn !== void 0 ? isOptedIn : true);
  const hasCPQ = hasCPQBeta || scopes.includes(Scopes.cpqQuoteCreate);
  const hasFeatureAccess = gates.includes(featureGate);
  return hasCPQ && hasFeatureAccess;
};
export const getHasCPQQuoteAccessWithFeature = (gates, featureGate, scopes, isOptedIn) => {
  const hasCPQBeta = gates.includes(CPQ_QUOTES_GATE) && (isOptedIn !== null && isOptedIn !== void 0 ? isOptedIn : true);
  const hasCPQ = hasCPQBeta || scopes.includes(Scopes.cpqQuoteAccess);
  const hasFeatureAccess = gates.includes(featureGate);
  return hasCPQ && hasFeatureAccess;
};